import React, { type ReactNode } from 'react';
import { useIntl } from 'react-intl';

import { Hide } from '@atlaskit/primitives/compiled/hide';
import { Text } from '@atlaskit/primitives/compiled/text';
import Image from '@atlaskit/image/image';
import FadeIn from '@atlaskit/motion/fade-in';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { useTour } from '../../hooks/useNthUserOrientationStore.ts';
import { spotlight, editorSpaceSpotlight } from '../../messages.ts';
import { useSpotlightAnalytics } from '../../hooks/useSpotlightAnalytics.ts';
import { usePreloadGif } from '../../hooks/usePreloadGif.ts';
import spacesLight from '../../assets/images/Spaces.gif';
import spacesDark from '../../assets/images/Spaces-dark.gif';
import spacesPlaceholderLight from '../../assets/images/Spaces-placeholder.png';
import spacesPlaceholderDark from '../../assets/images/Spaces-placeholder-dark.png';

import { SpotlightStepCount } from '@atlaskit/spotlight/step-count';
import { SpotlightPrimaryAction } from '@atlaskit/spotlight/primary-action';
import { SpotlightMedia } from '@atlaskit/spotlight/media';
import { SpotlightHeadline } from '@atlaskit/spotlight/headline';
import { SpotlightHeader } from '@atlaskit/spotlight/header';
import { SpotlightFooter } from '@atlaskit/spotlight/footer';
import { SpotlightDismissControl } from '@atlaskit/spotlight/dismiss-control';
import { SpotlightControls } from '@atlaskit/spotlight/controls';
import { SpotlightCard } from '@atlaskit/spotlight/card';
import { SpotlightBody } from '@atlaskit/spotlight/body';
import { SpotlightActions } from '@atlaskit/spotlight/actions';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

interface EditorSpaceSpotlightProps {
	children: ReactNode;
}
const STEP_ID = 'nth-user-orientation-editor-space-spotlight';
/**
 * EditorSpaceSpotlight component that displays a spotlight tutorial for the editor space.
 * It wraps the target element and shows a spotlight popover when the current tour step matches.
 */
export const EditorSpaceSpotlight: React.FC<EditorSpaceSpotlightProps> = ({ children }) => {
	const { reset, next, currentStep, progress, hasInteracted, active } = useTour(STEP_ID);
	const { formatMessage } = useIntl();
	const isVisible = currentStep === STEP_ID;
	const { PopoverContent } = useChoreographerAssets();

	const currentSrc = usePreloadGif({
		gif: { light: spacesLight, dark: spacesDark },
		placeholder: { light: spacesPlaceholderLight, dark: spacesPlaceholderDark },
		skip: !active,
	});

	const { handleNext, handleDismiss } = useSpotlightAnalytics({
		spotlightId: STEP_ID,
		isVisible,
		onNext: next,
		onDismiss: reset,
	});

	return (
		<PopoverProvider>
			<PopoverTarget>{children}</PopoverTarget>
			<Hide below="md">
				<PopoverContent
					shouldDismissOnClickOutside={false}
					dismiss={handleDismiss}
					next={handleNext}
					placement="right-end"
					offset={[0, 24]}
					isVisible={isVisible}
					messageId={STEP_ID}
					messageType={hasInteracted ? 'transactional' : 'engagement'}
				>
					{(() => {
						const spotlightCard = (
							<SpotlightCard testId={STEP_ID}>
								<SpotlightHeader>
									<SpotlightHeadline>
										{formatMessage(editorSpaceSpotlight.headline)}
									</SpotlightHeadline>
									<SpotlightControls>
										<SpotlightDismissControl />
									</SpotlightControls>
								</SpotlightHeader>
								<SpotlightMedia>
									<Image src={currentSrc} alt="" />
								</SpotlightMedia>
								<SpotlightBody>
									<Text>{formatMessage(editorSpaceSpotlight.body)}</Text>
								</SpotlightBody>
								<SpotlightFooter>
									<SpotlightStepCount>
										{formatMessage(spotlight.progress, progress)}
									</SpotlightStepCount>
									<SpotlightActions>
										<SpotlightPrimaryAction>{formatMessage(spotlight.next)}</SpotlightPrimaryAction>
									</SpotlightActions>
								</SpotlightFooter>
							</SpotlightCard>
						);
						return fg('platform-dst-motion-uplift-spotlight') ? (
							spotlightCard
						) : (
							<FadeIn entranceDirection="left">
								{(props) => <div {...props}>{spotlightCard}</div>}
							</FadeIn>
						);
					})()}
				</PopoverContent>
			</Hide>
		</PopoverProvider>
	);
};
